import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";

import { portalApi } from "@/features/customers/api";
import { onDay } from "@/features/customers/format";
import {
  MinutesBar,
  fmtMinutes,
  minuteLevel,
  minuteTextClass,
} from "@/features/customers/minutes";
import { useAuthStore } from "@/features/auth/store";
import { cn } from "@/lib/utils";

/** One line of "how many minutes are left", on the customer's dashboard.
 *
 * Discovery, not decoration: when calls stop, the reason has to be somewhere a
 * customer already looks. Renders nothing for a tenant user, and nothing for a
 * customer with no plan — neither has an allowance to run out of.
 */
export default function MyMinutesStrip() {
  const role = useAuthStore((s) => s.user?.role);
  const isCustomer = role === "customer";
  const q = useQuery({
    queryKey: ["my-plan"],
    queryFn: portalApi.myPlan,
    enabled: isCustomer,
    staleTime: 15_000,
  });

  if (!isCustomer || !q.data?.metered) return null;

  const u = q.data;
  const level = minuteLevel(u);
  return (
    <Link
      to="/my-plan"
      className={cn(
        "flex items-center gap-4 rounded-lg border p-4 transition-colors hover:bg-muted/40",
        level === "empty" && "border-destructive/40 bg-destructive/5",
      )}
    >
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <p className="text-sm font-medium">
            {level === "empty" ? (
              <span className="text-destructive">
                Call minutes used up — new calls are being refused
              </span>
            ) : (
              <>
                <span className="tabular-nums">{fmtMinutes(u.minutes_left)}</span> call
                minutes left
              </>
            )}
          </p>
          <p className={cn("text-xs tabular-nums", minuteTextClass(level))}>
            {fmtMinutes(u.minutes_used)} of {fmtMinutes(u.minutes_allowed)} used ·
            resets {onDay(u.period_end)}
          </p>
        </div>
        <MinutesBar usage={u} />
      </div>
      <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
    </Link>
  );
}
